import type { AdminSubmission } from '@l2u/ui/lib/types.ts';
import { hasPublishedRework } from '@l2u/ui/lib/reviewState.ts';

type ReplyFields = Pick<
  AdminSubmission,
  | 'latest_comment_author'
  | 'status'
  | 'resubmitted_at'
  | 'reviewed_at'
  | 'current_round_latest_author'
>;

/**
 * True when the ball is in the admin's court: the vendor sent the most recent
 * comment on this submission and the admin hasn't responded. Drives the red
 * "Vendor replied — awaiting your reply" dot in the admin review table /
 * submission rows and the "N awaiting your reply" pill.
 *
 * 'approved' is the per-document "Compliant" state (the green review button).
 * For an ordinary questionnaire answer, approval means "stop nudging": no dot
 * even if the vendor commented last. A compliance document (isComplianceDoc =
 * the question's include_in_compliance_documents) is different: the vendor can
 * keep talking to the reviewer about it from the Documents tab after approval,
 * so an approved compliance document lights the dot iff the vendor spoke last
 * in the CURRENT ROUND (current_round_latest_author, computed by the API from
 * comments newer than the latest approval; an unpublished admin draft counts as
 * the admin's answer). An older-round vendor message, or an API that does not
 * send the field, leaves it dark. The document stays approved — this is only
 * the reply signal; status, reviewTurn and company status do not move.
 *
 * Also returns true when the vendor explicitly resubmitted the answer (resubmitted_at
 * is set) and it's not yet approved — this lights the signal even with no new comment,
 * since the resubmit is itself the vendor's action.
 */
export function awaitingAdminReply(
  submission: ReplyFields | undefined,
  isComplianceDoc: boolean,
): boolean {
  if (!submission) return false;
  if (submission.status === 'approved') {
    return isComplianceDoc && submission.current_round_latest_author === 'vendor';
  }
  // An explicit resubmit lights the signal until the admin responds. Any
  // reviewed_at newer than the resubmit (a verdict OR a standalone comment)
  // resolves it; verdicts also clear resubmitted_at outright. Shared with
  // isAwaitingVendor and outstandingActionCount so the admin's "Reply needed"
  // and the vendor's "Action required" can never disagree about who owes what.
  if (hasPublishedRework(submission)) return true;
  return submission.latest_comment_author === 'vendor';
}

/** How many of a vendor's submissions are awaiting an admin reply. */
export function awaitingReplyCount(
  submissions: (ReplyFields & { question: { include_in_compliance_documents: boolean } })[],
): number {
  return submissions.reduce(
    (n, s) => (awaitingAdminReply(s, s.question.include_in_compliance_documents) ? n + 1 : n),
    0,
  );
}
